import CustomButton from 'components/custom-button/CustomButton';
import CustomInput, { InputType } from 'components/custom-input.tsx/CustomInput';
import React, { useEffect, useRef, useState } from 'react';

export interface LocationFilter {
  zipCode: string;
  rangeInMiles: number;
}

interface LocationFilterPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (location: LocationFilter) => void;
  initialLocation?: LocationFilter;
  anchorEl: HTMLElement | null;
}

const LocationFilterPopover: React.FC<LocationFilterPopoverProps> = ({
  isOpen,
  onClose,
  onApply,
  initialLocation,
  anchorEl
}) => {
  const [zipCode, setZipCode] = useState(initialLocation?.zipCode || '');
  const [rangeInMiles, setRangeInMiles] = useState(initialLocation?.rangeInMiles || 50);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setZipCode(initialLocation?.zipCode || '');
      setRangeInMiles(initialLocation?.rangeInMiles || 50);
    }
  }, [isOpen, initialLocation]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorEl]);

  const handleZipCodeChange = (value: string) => {
    setZipCode(value);
  };

  const handleApply = () => {
    onApply({ zipCode, rangeInMiles });
    onClose();
  };

  if (!isOpen || !anchorEl) return null;

  return (
    <div className="">
      <h2 className="mb-4 px-2.5 text-sbase font-medium text-primaryText">Filter by service location</h2>

      {/* Zip Code Input */}
      <div className="mb-4 px-2.5">
        <label htmlFor="zipCode" className="mb-1 block text-sm font-medium text-gray-700">
          Zip code
        </label>
        <CustomInput
          id="zipCode"
          type={InputType.TEXT}
          value={zipCode}
          onChange={handleZipCodeChange}
          placeholder="Enter zip code"
          className="h-7 w-full"
          aria-label="Enter zip code"
        />
      </div>

      {/* Range Input */}
      <div className="mb-4 px-2.5">
        <label htmlFor="rangeWithin" className="mb-1 block text-sm font-medium text-gray-700">
          Range within
        </label>
        <div className="flex items-center">
          <CustomInput
            id="rangeWithin"
            value={rangeInMiles}
            onChange={value => {
              const numValue = Number(value);
              if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                setRangeInMiles(numValue);
              }
            }}
            className="h-7 w-20 placeholder:text-accentText"
            aria-label="Range in miles"
          />
          <span className="ml-2 text-sm text-gray-500">Miles</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-x-2 bg-secondaryBtn px-3">
        <CustomButton
          id="location-filter-cancel-button"
          type={null}
          onClick={onClose}
          className="px-3 py-1 text-sm font-bold text-accentText"
          aria-label="Cancel">
          Cancel
        </CustomButton>
        <CustomButton
          id="location-filter-apply-button"
          type={null}
          onClick={handleApply}
          className="px-3 py-1 text-sm font-bold text-secondary"
          aria-label="Apply filters">
          Apply
        </CustomButton>
      </div>
    </div>
  );
};

export default LocationFilterPopover;
